import { ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { usePrefixedLocation } from "../hooks/usePrefixedLocation";
import { ROUTE_PREFIX } from "../constants/routes";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  Bell,
  Search,
  ChevronDown,
  LogOut,
  Home,
  User,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export interface LayoutMenuItem {
  icon: LucideIcon;
  label: string;
  path: string;
  subItems?: { label: string; path: string }[];
}

export interface RoleBasedLayoutProps {
  children: ReactNode;
  menuItems: LayoutMenuItem[];
  sidebarTitle: string;
  sidebarSubtitle: string;
  /** Where to redirect after sign out (e.g. "/" or "/portal-selection") */
  signOutRedirect?: string;
  /** Optional avatar fallback text (e.g. "SA" for Super Admin) */
  avatarFallback?: string;
  /** Show profile link in user dropdown (e.g. for Institution Admin) */
  showProfileInDropdown?: boolean;
  profilePath?: string;
}

export default function RoleBasedLayout({
  children,
  menuItems,
  sidebarTitle,
  sidebarSubtitle,
  signOutRedirect = "/",
  avatarFallback,
  showProfileInDropdown = false,
  profilePath,
}: RoleBasedLayoutProps) {
  const location = useLocation();
  const prefixedLocation = usePrefixedLocation();
  const navigate = usePrefixedNavigate();
  const { user, signOut } = useAuth();
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // Use pathname relative to app (strips tenant + ROUTE_PREFIX) so active state matches menu paths
  const pathname = location.pathname.includes(ROUTE_PREFIX)
    ? location.pathname.slice(
        location.pathname.indexOf(ROUTE_PREFIX) + ROUTE_PREFIX.length
      ) || "/"
    : prefixedLocation.pathname;

  const handleSignOut = async () => {
    await signOut();
    navigate(signOutRedirect);
  };

  const isActive = (path: string) =>
    pathname === path || pathname.startsWith(path + "/");

  const displayName = user?.email?.split("@")[0] || user?.phone || "Admin";
  const fallback = avatarFallback ?? displayName.slice(0, 2).toUpperCase();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar - same structure for all roles */}
      <aside className="fixed left-0 top-0 flex h-screen w-64 flex-col overflow-y-auto border-r border-border bg-card shadow-sm">
        <div className="border-b border-border p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
              <Home className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">{sidebarTitle}</h2>
              <p className="text-xs text-muted-foreground">{sidebarSubtitle}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-1.5 p-4">
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Menu
          </p>
          {menuItems.map(item => {
            const Icon = item.icon;
            const active =
              isActive(item.path) ||
              (item.subItems?.some(s => isActive(s.path)) ?? false);
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedItem === item.label;

            return (
              <div key={item.label}>
                <Button
                  variant={active ? "default" : "ghost"}
                  className={`h-11 w-full justify-start gap-3 ${
                    active
                      ? "bg-primary text-white shadow-sm hover:bg-primary/90"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                  onClick={() => {
                    if (hasSubItems) {
                      setExpandedItem(isExpanded ? null : item.label);
                    } else {
                      navigate(item.path);
                    }
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-left font-medium">
                    {item.label}
                  </span>
                  {hasSubItems && (
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  )}
                </Button>

                {hasSubItems && isExpanded && (
                  <div className="ml-6 mt-1 space-y-1">
                    {item.subItems?.map(subItem => (
                      <Button
                        key={subItem.path}
                        variant={isActive(subItem.path) ? "default" : "ghost"}
                        className={`h-10 w-full justify-start text-sm ${
                          isActive(subItem.path)
                            ? "bg-[#BB2124] text-white hover:bg-[#A01D20]"
                            : "text-muted-foreground hover:bg-muted"
                        }`}
                        onClick={() => navigate(subItem.path)}
                      >
                        {subItem.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-border p-4">
          <Button
            variant="ghost"
            className="h-11 w-full justify-start gap-3 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4" />
            <span className="font-medium">Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main content - same structure for all roles */}
      <div className="ml-64 flex-1">
        <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-sm">
          <div className="flex items-center justify-between px-6 py-3">
            <div className="max-w-md flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  className="h-10 border-0 bg-muted/50 pl-10 focus-visible:ring-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="relative h-10 w-10"
              >
                <Bell className="h-5 w-5 text-muted-foreground" />
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-10 gap-3 px-3 hover:bg-muted"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 font-semibold text-primary">
                        {fallback}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden text-left sm:block">
                      <div className="text-sm font-medium">{displayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {sidebarSubtitle}
                      </div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{displayName}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  {showProfileInDropdown && profilePath && (
                    <>
                      <DropdownMenuItem onClick={() => navigate(profilePath)}>
                        <User className="mr-2 h-4 w-4" />
                        My Profile
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="min-h-[calc(100vh-57px)] bg-muted/30 p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
